import { catalogue, licenceOf, resolutionText } from '../catalogue';
import type { SourceDef } from '../types';
import { getState } from '../state';
import { Sheet } from './sheet';
import { clear, el } from './dom';

const FAV_KEY = 'kmw.favourites';
const RECENT_KEY = 'kmw.recents';

function readList(key: string): string[] {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as string[]) : [];
  } catch {
    return [];
  }
}

function writeList(key: string, ids: string[]): void {
  try {
    localStorage.setItem(key, JSON.stringify(ids.slice(0, 8)));
  } catch {
    /* private mode — favourites are a convenience, not state we depend on */
  }
}

export function noteRecent(id: string): void {
  writeList(RECENT_KEY, [id, ...readList(RECENT_KEY).filter((x) => x !== id)]);
}

export interface PickerHandlers {
  onBase: (def: SourceDef) => void;
  onOverlay: (def: SourceDef) => void;
  onInfo: (def: SourceDef) => void;
}

export class Picker {
  readonly sheet: Sheet;
  private filter = '';
  private sortByResolution = false;
  private readonly list: HTMLElement;

  constructor(private readonly handlers: PickerHandlers) {
    this.sheet = new Sheet('Zdroje map');
    this.list = el('div', { class: 'picker-list' });

    const search = el('input', {
      class: 'picker-search',
      attrs: { type: 'search', placeholder: 'Hledat zdroj…', 'aria-label': 'Hledat zdroj' },
      on: {
        input: (ev: Event) => {
          this.filter = (ev.target as HTMLInputElement).value.trim().toLowerCase();
          this.render();
        },
      },
    });

    const sort = el('button', {
      class: 'picker-sort',
      attrs: { type: 'button' },
      text: 'Řadit dle rozlišení',
      on: {
        click: () => {
          this.sortByResolution = !this.sortByResolution;
          sort.classList.toggle('is-on', this.sortByResolution);
          this.render();
        },
      },
    });

    this.sheet.body.append(el('div', { class: 'picker-tools' }, [search, sort]), this.list);
    this.render();
  }

  open(): void {
    this.sheet.setSnap('half');
    this.render();
  }

  render(): void {
    const state = getState();
    clear(this.list);

    const matches = (d: SourceDef): boolean =>
      !this.filter ||
      `${d.title} ${d.provider} ${d.dateText} ${d.resolutionText}`.toLowerCase().includes(this.filter);

    const pinned = (key: string, title: string, min: number): void => {
      const ids = readList(key).filter((id) => catalogue.sources.some((s) => s.id === id));
      if (ids.length < min) return;
      const defs = ids
        .map((id) => catalogue.sources.find((s) => s.id === id)!)
        .filter(matches);
      if (defs.length) this.list.append(this.section(title, defs, state));
    };

    pinned(FAV_KEY, '★ Oblíbené', 1);
    // A single recent entry is just the row you are already looking at.
    pinned(RECENT_KEY, 'Naposledy použité', 2);

    for (const group of catalogue.groups) {
      const defs = catalogue.sources.filter((s) => s.group === group.id).filter(matches);
      if (!defs.length) continue;
      if (this.sortByResolution) {
        defs.sort((a, b) => (a.resolution ?? Infinity) - (b.resolution ?? Infinity));
      }
      this.list.append(this.section(group.title, defs, state));
    }

    if (!this.list.childElementCount) {
      this.list.append(el('p', { class: 'picker-empty', text: 'Nic neodpovídá hledání.' }));
    }
  }

  private section(title: string, defs: SourceDef[], state = getState()): HTMLElement {
    return el('div', { class: 'picker-section' }, [
      el('h3', { class: 'picker-section-title', text: title }),
      ...defs.map((d) => this.row(d, state)),
    ]);
  }

  private row(def: SourceDef, state = getState()): HTMLElement {
    const lic = licenceOf(def);
    const isBase = state.base.sourceId === def.id;
    const isOverlay = state.overlays.some((o) => o.sourceId === def.id);
    const timeId = isBase ? state.base.timeId : state.overlays.find((o) => o.sourceId === def.id)?.timeId;
    const favourites = readList(FAV_KEY);
    const isFav = favourites.includes(def.id);

    const main = el('button', {
      class: 'row-main',
      attrs: { type: 'button' },
      on: {
        click: () => {
          noteRecent(def.id);
          this.handlers.onBase(def);
        },
      },
    }, [
      el('div', { class: 'row-title' }, [
        el('span', { text: def.title }),
        def.kind === 'collection' ? el('span', { class: 'row-tag', text: 'časová řada' }) : null,
      ]),
      el('div', { class: 'row-meta' }, [
        el('strong', { text: resolutionText(def, timeId) }),
        el('span', { text: ` · ${def.dateText}` }),
      ]),
      el('div', { class: 'row-lic' }, [
        el('span', { text: def.provider }),
        el('span', { class: 'row-dot', text: ' · ' }),
        el('span', {
          class: lic.free ? 'row-licence' : 'row-licence is-restricted',
          text: lic.label,
        }),
        lic.warn ? el('span', { class: 'row-licence-warn', text: ` ${lic.warn}` }) : null,
      ]),
      def.highlight ? el('div', { class: 'row-highlight', text: def.highlight }) : null,
      def.warnText ? el('div', { class: 'row-warn', text: `⚠ ${def.warnText}` }) : null,
    ]);

    const fav = el('button', {
      class: isFav ? 'row-icon is-on' : 'row-icon',
      attrs: { type: 'button', 'aria-label': 'Oblíbené' },
      text: isFav ? '★' : '☆',
      on: {
        click: () => {
          const next = isFav ? favourites.filter((x) => x !== def.id) : [def.id, ...favourites];
          writeList(FAV_KEY, next);
          this.render();
        },
      },
    });

    const info = el('button', {
      class: 'row-icon',
      attrs: { type: 'button', 'aria-label': 'Podrobnosti o zdroji' },
      text: 'ⓘ',
      on: { click: () => this.handlers.onInfo(def) },
    });

    const add = el('button', {
      class: isOverlay ? 'row-icon is-on' : 'row-icon',
      attrs: { type: 'button', 'aria-label': 'Přidat jako překryv' },
      text: isOverlay ? '−' : '+',
      on: {
        click: () => {
          noteRecent(def.id);
          this.handlers.onOverlay(def);
        },
      },
    });

    return el('div', { class: `picker-row${isBase ? ' is-active' : ''}${isOverlay ? ' is-overlay' : ''}` }, [
      main,
      el('div', { class: 'row-actions' }, [fav, info, add]),
    ]);
  }
}
